import type {
  Block,
  Blocks,
  CommentMessage,
  CommentRefBlock,
  CommentThread,
  CommentsBlock,
} from './block'
import { blockId } from './block'
import { traverseBlocks, traverseBlocksDepthFirst } from './traverse'

export interface BlockCommentThread {
  anchor: Block
  ref: CommentRefBlock
  thread: CommentThread
}

/** Resolves inline comment refs and groups them with their semantic anchors. */
export function getBlockCommentThreads(blocks: Blocks): BlockCommentThread[] {
  const registry = getCommentsBlock(blocks)
  if (!registry) return []
  const threadsById = new Map(
    registry.threads.map((thread) => [thread.threadId, thread]),
  )
  const result: BlockCommentThread[] = []

  traverseBlocksDepthFirst(blocks, 'comment', (ref, ops) => {
    const thread = threadsById.get(ref.commentId)
    if (!thread) return
    result.push({ anchor: ops.getParent(), ref, thread })
  })

  return result
}

export function getCommentsBlock(blocks: Blocks): CommentsBlock | undefined {
  return blocks.find(
    (block): block is CommentsBlock => block.type === 'comments',
  )
}

export function getPageCommentThreads(blocks: Blocks): CommentThread[] {
  return getCommentsBlock(blocks)?.threads.filter(thread => thread.scope === 'page') ?? []
}

export function addPageCommentThread(blocks: Blocks, thread: CommentThread): Blocks {
  if (thread.scope !== 'page') throw new Error('page comment thread requires page scope')
  const next = structuredClone(blocks)
  const registry = getCommentsBlock(next)
  if (registry) registry.threads.push(thread)
  else next.push({ id: blockId(), type: 'comments', threads: [thread] })
  validateCommentDocument(next)
  return next
}

/** Validates a complete document, including refs and registry uniqueness. */
export function validateCommentDocument(blocks: Blocks): void {
  const registries: CommentsBlock[] = []
  const refs: CommentRefBlock[] = []
  let nestedRegistry = false

  traverseBlocks(blocks, (block, ops) => {
    if (block.type === 'comment') refs.push(block)
    if (block.type !== 'comments') return
    registries.push(block)
    if (ops.getParent().type !== 'root') nestedRegistry = true
  })

  if (nestedRegistry)
    throw new Error('comments registry must be document-level')
  if (registries.length > 1) {
    throw new Error('document must contain at most one comments registry')
  }
  const registry = registries[0]
  if (!registry) {
    if (refs.length > 0)
      throw new Error('comment ref requires comments registry')
    return
  }
  if (blocks.at(-1) !== registry) {
    throw new Error('comments registry must be the final document block')
  }

  const threadIds = new Set<string>()
  const messageIds = new Set<string>()
  for (const thread of registry.threads) {
    assertNonEmptyString(thread.threadId, 'comment thread id')
    if (threadIds.has(thread.threadId)) {
      throw new Error(`duplicate comment thread id: ${thread.threadId}`)
    }
    threadIds.add(thread.threadId)
    if (thread.status !== 'open' && thread.status !== 'resolved') {
      throw new Error(`invalid comment thread status: ${String(thread.status)}`)
    }
    if (thread.scope !== undefined && thread.scope !== 'page') {
      throw new Error(`invalid comment thread scope: ${String(thread.scope)}`)
    }
    if (!Array.isArray(thread.comments)) {
      throw new TypeError(
        `comment thread ${thread.threadId} comments must be an array`,
      )
    }
    for (const message of thread.comments) {
      assertNonEmptyString(message.id, 'comment message id')
      assertNonEmptyString(message.authorId, 'comment author id')
      if (!Number.isSafeInteger(message.createdAt) || message.createdAt < 0) {
        throw new TypeError(
          `comment ${message.id} createdAt must be Unix milliseconds`,
        )
      }
      if (typeof message.body !== 'string') {
        throw new TypeError(`comment ${message.id} body must be a string`)
      }
      if (message.editedAt !== undefined && (!Number.isSafeInteger(message.editedAt) || message.editedAt < 0)) {
        throw new TypeError(`comment ${message.id} editedAt must be Unix milliseconds`)
      }
      if (messageIds.has(message.id)) {
        throw new Error(`duplicate comment message id: ${message.id}`)
      }
      messageIds.add(message.id)
    }
  }

  const referenced = new Set<string>()
  for (const ref of refs) {
    assertNonEmptyString(ref.commentId, 'comment ref id')
    if (!threadIds.has(ref.commentId)) {
      throw new Error(`dangling comment ref: ${ref.commentId}`)
    }
    if (registry.threads.find(thread => thread.threadId === ref.commentId)?.scope === 'page') {
      throw new Error(`page comment cannot have inline ref: ${ref.commentId}`)
    }
    if (referenced.has(ref.commentId)) {
      throw new Error(`duplicate comment ref: ${ref.commentId}`)
    }
    referenced.add(ref.commentId)
  }
}

function assertNonEmptyString(
  value: unknown,
  label: string,
): asserts value is string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${label} must be a non-empty string`)
  }
}

export interface UpsertCommentThreadOptions {
  anchorBlockId: string
  anchorText?: string
  thread: CommentThread
  refId?: string
  commentsBlockId?: string
  paragraphId?: string
}

export type UpsertCommentRefOptions = Omit<
  UpsertCommentThreadOptions,
  'commentsBlockId' | 'thread'
> & { commentId: string; point?: boolean }

/** Adds or moves one inline ref. Thread data stays in its separate sidecar. */
export function upsertCommentRef(
  blocks: Blocks,
  options: UpsertCommentRefOptions,
): Blocks {
  assertNonEmptyString(options.commentId, 'comment ref id')
  const next = structuredClone(blocks)
  let existingRefId: string | undefined

  traverseBlocks(next, 'comment', (ref, ops) => {
    if (ref.commentId !== options.commentId) return
    existingRefId ??= ref.id
    ops.remove()
  })

  const anchors: Block[] = []
  traverseBlocks(next, (block) => {
    if (block.id === options.anchorBlockId) anchors.push(block)
  })
  if (anchors.length === 0) {
    throw new Error(`comment anchor not found: ${options.anchorBlockId}`)
  }
  if (anchors.length > 1) {
    throw new Error(`comment anchor id is ambiguous: ${options.anchorBlockId}`)
  }

  const ref: CommentRefBlock = {
    id: existingRefId ?? options.refId ?? blockId(),
    type: 'comment',
    commentId: options.commentId,
    children: [],
  }
  if (options.point && attachPointCommentRef(anchors[0], ref)) return next
  if (
    !attachCommentRef(anchors[0], ref, options.paragraphId, options.anchorText)
  ) {
    throw new Error(`block ${options.anchorBlockId} cannot anchor a comment`)
  }
  return next
}

function attachPointCommentRef(anchor: Block, ref: CommentRefBlock): boolean {
  if (
    anchor.type === 'p' ||
    anchor.type === 'h' ||
    anchor.type === 'td' ||
    anchor.type === 'diff'
  ) {
    anchor.children.push(ref)
    return true
  }
  if ('children' in anchor && anchor.children) {
    for (const child of anchor.children as Blocks) {
      if (attachPointCommentRef(child, ref)) return true
    }
  }
  return false
}

/** Adds or moves a thread ref and normalizes the registry to the document end. */
export function upsertCommentThread(
  blocks: Blocks,
  options: UpsertCommentThreadOptions,
): Blocks {
  assertValidThread(options.thread)
  const next = upsertCommentRef(blocks, {
    anchorBlockId: options.anchorBlockId,
    anchorText: options.anchorText,
    commentId: options.thread.threadId,
    paragraphId: options.paragraphId,
    refId: options.refId,
  })

  const registries = next.filter(
    (block): block is CommentsBlock => block.type === 'comments',
  )
  if (registries.length > 1) {
    throw new Error('document must contain at most one comments registry')
  }
  const existingRegistry = registries[0]
  const registry: CommentsBlock = {
    id: existingRegistry?.id ?? options.commentsBlockId ?? blockId(),
    type: 'comments',
    threads: upsertThread(existingRegistry?.threads ?? [], options.thread),
  }
  const result = [
    ...next.filter((block) => block.type !== 'comments'),
    registry,
  ]
  validateCommentDocument(result)
  return result
}

export function replyCommentThread(
  blocks: Blocks,
  threadId: string,
  comment: CommentMessage,
): Blocks {
  assertValidMessage(comment)
  const next = structuredClone(blocks)
  const registry = requiredCommentsBlock(next)
  if (
    registry.threads.some((thread) =>
      thread.comments.some((item) => item.id === comment.id),
    )
  ) {
    throw new Error(`duplicate comment message id: ${comment.id}`)
  }
  const thread = registry.threads.find((item) => item.threadId === threadId)
  if (!thread) throw new Error(`comment thread not found: ${threadId}`)
  thread.comments.push(comment)
  validateCommentDocument(next)
  return next
}

export function editCommentMessage(
  blocks: Blocks,
  threadId: string,
  messageId: string,
  body: string,
): Blocks {
  if (!body.trim()) throw new Error('comment body must not be empty')
  const next = structuredClone(blocks)
  const thread = requiredCommentThread(next, threadId)
  const message = thread.comments.find((item) => item.id === messageId)
  if (!message) throw new Error(`comment message not found: ${messageId}`)
  if (message.body !== body.trim()) {
    message.body = body.trim()
    message.editedAt = Date.now()
  }
  validateCommentDocument(next)
  return next
}

export function removeCommentMessage(blocks: Blocks, threadId: string, messageId: string): Blocks {
  const thread = requiredCommentThread(blocks, threadId)
  if (!thread.comments.some(message => message.id === messageId)) throw new Error(`comment message not found: ${messageId}`)
  if (thread.comments.length === 1) return removeCommentThread(blocks, threadId)
  const next = structuredClone(blocks)
  requiredCommentThread(next, threadId).comments = thread.comments.filter(message => message.id !== messageId)
  validateCommentDocument(next)
  return next
}

/** Accept is user-facing terminology; serialized status remains `resolved`. */
export function acceptCommentThread(blocks: Blocks, threadId: string): Blocks {
  return setCommentThreadStatus(blocks, threadId, 'resolved')
}

export function reopenCommentThread(blocks: Blocks, threadId: string): Blocks {
  return setCommentThreadStatus(blocks, threadId, 'open')
}

export function removeCommentThread(blocks: Blocks, threadId: string): Blocks {
  const next = structuredClone(blocks)
  traverseBlocks(next, 'comment', (ref, ops) => {
    if (ref.commentId === threadId) ops.remove()
  })
  const registry = requiredCommentsBlock(next)
  const previousLength = registry.threads.length
  registry.threads = registry.threads.filter((thread) => thread.threadId !== threadId)
  if (registry.threads.length === previousLength) {
    throw new Error(`comment thread not found: ${threadId}`)
  }
  const result =
    registry.threads.length === 0
      ? next.filter((block) => block !== registry)
      : next
  validateCommentDocument(result)
  return result
}

function setCommentThreadStatus(
  blocks: Blocks,
  threadId: string,
  status: CommentThread['status'],
): Blocks {
  const next = structuredClone(blocks)
  const registry = requiredCommentsBlock(next)
  const thread = registry.threads.find((item) => item.threadId === threadId)
  if (!thread) throw new Error(`comment thread not found: ${threadId}`)
  thread.status = status
  validateCommentDocument(next)
  return next
}

function requiredCommentsBlock(blocks: Blocks): CommentsBlock {
  const registry = getCommentsBlock(blocks)
  if (!registry) throw new Error('comments registry not found')
  return registry
}

function requiredCommentThread(
  blocks: Blocks,
  threadId: string,
): CommentThread {
  const thread = requiredCommentsBlock(blocks).threads.find(
    (item) => item.threadId === threadId,
  )
  if (!thread) throw new Error(`comment thread not found: ${threadId}`)
  return thread
}

function upsertThread(
  threads: CommentThread[],
  thread: CommentThread,
): CommentThread[] {
  const index = threads.findIndex((item) => item.threadId === thread.threadId)
  if (index < 0) return [...threads, thread]
  return threads.map((item, itemIndex) => (itemIndex === index ? thread : item))
}

function attachCommentRef(
  anchor: Block,
  ref: CommentRefBlock,
  paragraphId?: string,
  anchorText?: string,
): boolean {
  if ('children' in anchor && Array.isArray(anchor.children) && anchorText) {
    return wrapCommentText(anchor.children as Blocks, anchorText, ref)
  }
  if (anchor.type === 'p' || anchor.type === 'h' || anchor.type === 'td' ||
    anchor.type === 'diff' || (anchor.type === 'review' && anchor.display === 'inline')) {
    ref.children = anchor.children
    anchor.children = [ref]
    return true
  }
  if ('children' in anchor && Array.isArray(anchor.children)) {
    for (const child of anchor.children as Blocks) {
      if (attachCommentRef(child, ref, paragraphId, anchorText)) return true
    }
  }
  if (anchor.type === 'li') {
    anchor.children.push({ id: paragraphId ?? blockId(), type: 'p', children: [ref] })
    return true
  }
  return false
}

/** Display text used by review selection anchors, without markdown delimiters. */
export function commentAnchorText(block: Block): string {
  if (block.type === 'text') return block.text
  if (block.type === 'chars') return block.text.map(segment => segment.text).join('')
  return 'children' in block && Array.isArray(block.children)
    ? (block.children as Blocks).map(block => commentAnchorText(block)).join('') : ''
}

function wrapCommentText(children: Blocks, anchorText: string, ref: CommentRefBlock): boolean {
  const needle = anchorText.trim()
  const start = children.map(block => commentAnchorText(block)).join('').indexOf(needle)
  if (!needle || start < 0) return false
  const end = start + needle.length
  let offset = 0
  let pieceIndex = 0
  const wrap = (items: Blocks): Blocks => items.flatMap(child => {
    if (child.type === 'text' || child.type === 'chars') {
      const text = commentAnchorText(child)
      const from = Math.max(0, start - offset)
      const to = Math.min(text.length, end - offset)
      offset += text.length
      if (from >= to) return [child]
      const piece = (value: string, id: string): Block => child.type === 'chars'
        ? { ...child, id, text: [{ id: '', text: value }] }
        : { ...child, id, text: value }
      const result: Blocks = []
      if (from) result.push(piece(text.slice(0, from), ''))
      result.push({ ...ref, id: pieceIndex++ === 0 ? ref.id : `${ref.id}:${pieceIndex}`,
        children: [piece(text.slice(from, to), from === 0 && to === text.length ? child.id : '')] })
      if (to < text.length) result.push(piece(text.slice(to), ''))
      return result
    }
    if ('children' in child && Array.isArray(child.children)) {
      return [{ ...child, children: wrap(child.children as Blocks) } as Block]
    }
    return [child]
  })
  children.splice(0, children.length, ...wrap(children))
  return true
}

function assertValidThread(thread: CommentThread): void {
  validateCommentDocument([
    { id: 'comments-validation', type: 'comments', threads: [thread] },
  ])
}

function assertValidMessage(message: CommentMessage): void {
  assertValidThread({
    threadId: 'thread-validation',
    status: 'open',
    comments: [message],
  })
}
